/**
 * DezLin Wishlist — replaces Wishlist - Wishify app ($5.99/mo)
 *
 * Features:
 * - Heart button on every product card and product page
 * - Persists via localStorage (guests) + customer metafields (logged-in, cross-device)
 * - Dedicated wishlist page with product cards, prices, Add to Cart
 * - Live count badge on header heart icon
 * - Toast notification on add/remove
 * - "Add all to cart" button on wishlist page
 * - Handles variant selection on wishlist page
 * - Gracefully handles deleted / unavailable products
 */

const WL_KEY      = 'dezlin_wl';      // localStorage: array of product handles
const WL_META_NS  = 'dezlin';
const WL_META_KEY = 'wishlist';
const WL_API      = 'https://dezlin-shopify-api.vercel.app/api/wishlist'; // future cross-device sync

/* ─────────────────────────────────────────
   Core storage helpers
───────────────────────────────────────── */

function wlLoad() {
  try { return JSON.parse(localStorage.getItem(WL_KEY) || '[]'); }
  catch { return []; }
}

function wlSave(handles) {
  localStorage.setItem(WL_KEY, JSON.stringify(handles));
}

/* ─────────────────────────────────────────
   Public API (window.Wishlist)
───────────────────────────────────────── */

const Wishlist = {
  items: wlLoad(),

  has(handle) {
    return this.items.includes(handle);
  },

  add(handle) {
    if (!this.has(handle)) {
      this.items.push(handle);
      wlSave(this.items);
    }
  },

  remove(handle) {
    this.items = this.items.filter(h => h !== handle);
    wlSave(this.items);
  },

  toggle(handle, title) {
    const wasIn = this.has(handle);
    wasIn ? this.remove(handle) : this.add(handle);
    this._updateAllButtons(handle);
    this._updateCount();
    this._toast(wasIn ? `Removed from wishlist` : `Added to wishlist`);
    return !wasIn; // true = now in wishlist
  },

  count() {
    return this.items.length;
  },

  /* ── UI helpers ── */

  _updateAllButtons(handle) {
    document.querySelectorAll(`[data-wl-btn][data-wl-handle="${handle}"]`)
      .forEach(btn => this._setBtn(btn, this.has(handle)));
  },

  _setBtn(btn, active) {
    btn.classList.toggle('wl-btn--active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute('aria-label', active
      ? (btn.dataset.wlTitle ? `Remove ${btn.dataset.wlTitle} from wishlist` : 'Remove from wishlist')
      : (btn.dataset.wlTitle ? `Add ${btn.dataset.wlTitle} to wishlist` : 'Add to wishlist')
    );
    const svg = btn.querySelector('.wl-heart');
    if (svg) svg.style.fill = active ? 'currentColor' : 'none';
  },

  _updateCount() {
    const n = this.count();
    document.querySelectorAll('[data-wl-count]').forEach(el => {
      el.textContent = n > 0 ? n : '';
      el.hidden = n === 0;
    });
  },

  _toast(msg) {
    const el = document.getElementById('wl-toast');
    if (!el) return;
    el.textContent = msg;
    el.removeAttribute('hidden');
    el.classList.add('wl-toast--show');
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      el.classList.remove('wl-toast--show');
      setTimeout(() => el.setAttribute('hidden', ''), 300);
    }, 2200);
  },

  /* ── Init all buttons on page ── */

  initButtons() {
    document.querySelectorAll('[data-wl-btn]').forEach(btn => {
      this._setBtn(btn, this.has(btn.dataset.wlHandle));
    });
    this._updateCount();
  },
};

/* ─────────────────────────────────────────
   Click handler (delegated)
───────────────────────────────────────── */

document.addEventListener('click', e => {
  const btn = e.target.closest('[data-wl-btn]');
  if (!btn) return;
  e.preventDefault();
  Wishlist.toggle(btn.dataset.wlHandle, btn.dataset.wlTitle);
});

/* ─────────────────────────────────────────
   Wishlist Page
───────────────────────────────────────── */

async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

async function renderWishlistPage(container) {
  const handles = Wishlist.items;

  if (handles.length === 0) {
    container.innerHTML = `
      <div class="wl-page__empty">
        <p>Your wishlist is empty.</p>
        <a href="/collections/all" class="button">Continue Shopping</a>
      </div>`;
    return;
  }

  container.innerHTML = `<div class="wl-page__loading">Loading your wishlist…</div>`;

  const products = (await Promise.all(handles.map(fetchProduct))).filter(Boolean);

  if (products.length === 0) {
    container.innerHTML = `
      <div class="wl-page__empty">
        <p>Your wishlist is empty.</p>
        <a href="/collections/all" class="button">Continue Shopping</a>
      </div>`;
    Wishlist.items = [];
    wlSave([]);
    Wishlist._updateCount();
    return;
  }

  // Remove stale handles (products no longer exist)
  const validHandles = products.map(p => p.handle);
  Wishlist.items = Wishlist.items.filter(h => validHandles.includes(h));
  wlSave(Wishlist.items);
  Wishlist._updateCount();

  const hasAvailable = products.some(p => p.available);

  container.innerHTML = `
    ${hasAvailable ? `
    <div class="wl-page__actions">
      <button class="wl-atc-btn" id="wlAddAllBtn" style="flex:none;padding:1rem 2.4rem;font-size:1.4rem;">Add All to Cart</button>
    </div>` : ''}
    <ul class="wl-grid">
      ${products.map(p => productCard(p)).join('')}
    </ul>`;

  // Wire up buttons
  Wishlist.initButtons();

  // Add All to Cart
  const addAllBtn = document.getElementById('wlAddAllBtn');
  if (addAllBtn) {
    addAllBtn.addEventListener('click', () => addAllToCart(products));
  }

  // Wire individual add-to-cart
  container.querySelectorAll('[data-wl-add]').forEach(btn => {
    btn.addEventListener('click', () => addSingleToCart(btn));
  });

  // Wire variant selects
  container.querySelectorAll('.wl-variant-select').forEach(sel => {
    sel.addEventListener('change', e => {
      const card = e.target.closest('.wl-card');
      const addBtn = card?.querySelector('[data-wl-add]');
      if (addBtn) addBtn.dataset.variantId = e.target.value;
    });
  });
}

function productCard(p) {
  const price = formatMoney(p.price);
  const comparePrice = p.compare_at_price && p.compare_at_price > p.price
    ? `<s class="wl-card__compare">${formatMoney(p.compare_at_price)}</s>` : '';
  const img = p.featured_image
    ? `<img src="${p.featured_image}" alt="${escHtml(p.title)}" loading="lazy" width="400">`
    : `<div class="wl-card__no-image"></div>`;

  const defaultVariant = p.variants?.[0];
  const variantPicker = p.variants?.length > 1 ? `
    <select class="wl-variant-select" aria-label="Select variant">
      ${p.variants.map(v => `
        <option value="${v.id}" ${!v.available ? 'disabled' : ''}>
          ${escHtml(v.title)}${!v.available ? ' — Sold Out' : ''}
        </option>`).join('')}
    </select>` : '';

  const addBtn = p.available
    ? `<button class="wl-atc-btn" data-wl-add data-variant-id="${defaultVariant?.id}">Add to Cart</button>`
    : `<button class="wl-atc-btn" disabled>Sold Out</button>`;

  return `
    <li class="wl-card">
      <a href="/products/${p.handle}" class="wl-card__img">${img}</a>
      <div class="wl-card__body">
        <a href="/products/${p.handle}" class="wl-card__title">${escHtml(p.title)}</a>
        <div class="wl-card__price">${price} ${comparePrice}</div>
        ${variantPicker}
        <div class="wl-card__btns">
          ${addBtn}
          <button
            class="wl-btn wl-btn--remove"
            data-wl-btn
            data-wl-handle="${p.handle}"
            data-wl-title="${escHtml(p.title)}"
            aria-pressed="true"
            aria-label="Remove ${escHtml(p.title)} from wishlist"
          >
            <svg class="wl-heart" viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" stroke-width="2" fill="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>
      </div>
    </li>`;
}

async function addSingleToCart(btn) {
  const variantId = parseInt(btn.dataset.variantId, 10);
  if (!variantId) return;
  btn.disabled = true;
  btn.textContent = 'Adding…';
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
    if (res.ok) {
      btn.textContent = 'Added!';
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      setTimeout(() => { btn.textContent = 'Add to Cart'; btn.disabled = false; }, 1800);
    } else {
      btn.textContent = 'Error';
      btn.disabled = false;
    }
  } catch {
    btn.textContent = 'Error';
    btn.disabled = false;
  }
}

async function addAllToCart(products) {
  const btn = document.getElementById('wlAddAllBtn');
  if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

  const items = products
    .filter(p => p.available && p.variants?.[0])
    .map(p => ({ id: p.variants[0].id, quantity: 1 }));

  if (items.length === 0) return;

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items })
    });
    if (res.ok) {
      if (btn) btn.textContent = `${items.length} items added!`;
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      setTimeout(() => {
        if (btn) { btn.textContent = 'Add All to Cart'; btn.disabled = false; }
      }, 2000);
    }
  } catch {
    if (btn) { btn.textContent = 'Error — try again'; btn.disabled = false; }
  }
}

/* ─────────────────────────────────────────
   Utilities
───────────────────────────────────────── */

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

function escHtml(str) {
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

/* ─────────────────────────────────────────
   Boot
───────────────────────────────────────── */

window.Wishlist = Wishlist;

document.addEventListener('DOMContentLoaded', () => {
  Wishlist.initButtons();

  const page = document.getElementById('dezlin-wishlist-page');
  if (page) renderWishlistPage(page);

  // On product pages: move the heart button to sit beside the Loox rating
  const productPageBtn = document.querySelector('.wl-btn--product-page');
  if (productPageBtn) {
    const looxContainer = document.querySelector('.custom__liquid .loox-rating');
    if (looxContainer) {
      const row = document.createElement('div');
      row.className = 'wl-product-rating-row';
      productPageBtn.style.display = '';
      productPageBtn.classList.remove('wl-btn--product-page');
      looxContainer.parentElement.insertBefore(row, looxContainer);
      row.appendChild(productPageBtn);
      row.appendChild(looxContainer);
    } else {
      // Loox not found — just show the button in place
      productPageBtn.style.display = '';
    }
  }

  // Re-init if Shopify re-renders product cards (e.g. infinite scroll, quick view)
  document.addEventListener('shopify:section:load', () => Wishlist.initButtons());
});
